import React, {useEffect, useRef, useState} from "react";
import {Button, Card, Col, Form, Row, Spinner} from 'react-bootstrap'
import {useAlert} from "react-alert";
import {Header} from "../../../genericComponents";
import EmailEditor from 'react-email-editor'
import {
  getChannelsListComboBox,
  getMessageConfigId,
  getMessageTemplatesListComboBox,
  getMessageConfigListComboBox,
  postMessageConfig,
  putMessageConfig
} from "../../../services/RequestService";

export default function IntraFlowConfig() {
  const alert = useAlert();
  const emailEditorRef = useRef(null);

  const layoutVars = [
    'idProcesso', 'tipoProcesso', 'statusProcesso', 'idCliente', 'nome', 'aniversario',
    'email', 'telefone', 'dataEntrega', 'dataCriacaoProcesso', 'dataPausaProcesso',
    'processoPausado', 'processoFinalizado', 'valorPagamento', 'rmiSimulada', 'rmiConcedida',
    'complementoPositivo', 'tipoPagamento', 'urlBoleto', 'tempoAposentadoriaMeses', 'cenario'
  ]
  const shootingList = [
    {value: 'MANUAL', label: "Manual"},
    {value: 'RADAR_MONTH', label: "Radar Mensal"},
    {value: 'BIRTHDAY', label: "Aniversário"}
  ]
  const modelForm = {
    id: null,
    templateName: null,
    shooting: null,
    status: null,
    channelId: null,
    whatsappTemplate: null,
    templateParams: null,
    emailSubject: null,
    emailBody: null,
    emailDesign: null,
    sendEmail: false,
  }

  const [loading, setLoading] = useState(false)
  const [form, setForm] = useState({...modelForm});
  const [validated, setValidated] = useState(false);
  const [templates, setTemplates] = useState(null);
  const [channels, setChannels] = useState([]);
  const [metaTemplates, setMetaTemplates] = useState([]);

  useEffect(() => {
    if (templates === null) {
      setLoading(true)
      getMessageConfigListComboBox().then(({data, ok}) => {
        if (ok) setTemplates(data)
        getChannelsListComboBox().then(({data, ok}) => {
          if (ok) setChannels(data)
          setLoading(false)
        })
      })
    }
  }, [templates]);

  // Os modelos aprovados são por canal, então a lista recarrega sempre que o canal muda
  // (inclusive ao carregar um layout já gravado pelo filtro do topo).
  useEffect(() => {
    if (!form.channelId) {
      setMetaTemplates([])
      return
    }
    let ativo = true
    getMessageTemplatesListComboBox(form.channelId).then(({data, ok}) => {
      if (ativo) setMetaTemplates(ok && Array.isArray(data) ? data : [])
    })
    return () => {
      ativo = false
    }
  }, [form.channelId]);

  // O de-para é persistido como texto (uma variável por linha) na coluna template_params.
  const paramRows = form.templateParams ? form.templateParams.split('\n') : [];

  function setParamRows(rows) {
    onChange('templateParams', rows.length ? rows.join('\n') : null);
  }

  // Modelo escolhido no combo (quando veio da API). É ele que traz o variableCount.
  const selectedTemplate = metaTemplates.find(t => t.value === form.whatsappTemplate);

  // Ao escolher um modelo aprovado, a API já diz quantas variáveis ({{1}}, {{2}}…) ele tem, então
  // dimensionamos as linhas de de-para automaticamente, preservando os mapeamentos já feitos.
  function onSelectTemplate(templateName) {
    const tpl = metaTemplates.find(t => t.value === templateName);
    setForm(prev => {
      const next = {...prev, whatsappTemplate: templateName || null};
      if (tpl) {
        const current = prev.templateParams ? prev.templateParams.split('\n') : [];
        const rows = Array.from({length: tpl.variableCount}, (_, i) => current[i] || '');
        next.templateParams = rows.length ? rows.join('\n') : null;
      }
      return next;
    });
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setValidated(true);

    const requiredFields = ['templateName', 'whatsappTemplate', 'channelId', 'status', 'shooting'];
    // Assunto só é obrigatório quando o e-mail está ligado (senão os campos nem aparecem).
    if (form.sendEmail !== false) requiredFields.push('emailSubject');
    const isValid = requiredFields.every(f => form[f] !== null && form[f] !== '' && form[f] !== undefined);

    if (!isValid) return;

    // Linha em branco no meio deslocaria as posições seguintes ({{3}} viraria {{2}} no envio),
    // porque o backend descarta linhas vazias antes de montar o templateParams.
    if (paramRows.some(r => !r.trim())) {
      alert.error('Preencha ou remova as variáveis em branco do modelo');
      return;
    }

    const persist = async (payload) => {
      if (!form.id) {
        const {data, ok} = await postMessageConfig(payload)
        if (ok) {
          onChange('id', data.id)
          setTemplates(prev => [...(prev || []), {label: form.templateName, value: data.id}])
          alert.success('Layout adicionado com sucesso');
        } else {
          alert.error('Erro ao adicionar Layout, ' + data?.log);
        }
      } else {
        const {ok, data} = await putMessageConfig(payload, form.id)
        if (ok) alert.success('Layout alterado com sucesso');
        else alert.error('Erro ao alterar Layout, ' + data?.log);
      }
      setLoading(false);
    };

    setLoading(true);
    // Com o e-mail desligado o editor nem é renderizado, então salva o form direto.
    if (form.sendEmail === false || !emailEditorRef.current?.editor) {
      await persist({...form});
    } else {
      emailEditorRef.current.editor.exportHtml(async ({html, design}) => {
        await persist({...form, emailBody: html, emailDesign: JSON.stringify(design)});
      });
    }
  }

  function onChange(key, value) {
    setForm(prev => ({...prev, [key]: value}));
  }

  async function onFilter(id) {
    if (id) {
      setLoading(true)
      const {ok, data} = await getMessageConfigId(id)
      if (ok) setForm(data);
      setLoading(false)
    } else {
      clearForm()
    }
  }

  function clearForm() {
    setValidated(false);
    setForm({...modelForm})
    if (emailEditorRef.current?.editor) {
      emailEditorRef.current.editor.loadBlank({backgroundColor: '#e7e7e7'})
    }
  }

  function onReadyEmailEditor() {
    emailEditorRef.current.editor.loadDesign(
      typeof form.emailDesign === 'string' ? JSON.parse(form.emailDesign) : form.emailDesign
    )
  }

  const isInvalid = (field) =>
    validated && (form[field] === null || form[field] === '' || form[field] === undefined);

  return (
    <div style={{position: 'relative'}}>
      {loading && (
        <div style={{
          position: 'fixed', top: 0, left: 0,
          width: '100%', height: '100%',
          backgroundColor: 'rgba(255,255,255,0.6)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 9999,
        }}>
          <Spinner animation="border" variant="primary"/>
        </div>
      )}

      <Header active="Configuração IntraFlow" logged/>

      <div style={{height: window.innerHeight - 100 + 'px', overflowX: 'auto'}}>
        <Form noValidate validated={validated} onSubmit={handleSubmit}>
          <Card className="box-card mx-3 my-3">
            <Card.Header>
              <Row className="align-items-center">
                <Col xs={12} sm={8} className="p-0">Layout de Mensagens</Col>
                <Col xs={12} sm={4} className="p-0">
                  <Form.Control
                    as="select"
                    value={form.id || ''}
                    onChange={e => onFilter(e.target.value || null)}
                  >
                    <option value="">Filtrar</option>
                    {(templates || []).map(el => (
                      <option key={el.value} value={el.value}>{el.label}</option>
                    ))}
                  </Form.Control>
                </Col>
              </Row>
            </Card.Header>

            <Card.Body>
              {/* Variáveis disponíveis — usadas tanto no corpo do e-mail quanto nas variáveis do modelo */}
              <div className="border rounded p-2 mb-3 bg-light">
                <small><b>Variáveis do Layout</b></small>
                <div className="mt-1">
                  {layoutVars.map(v => (
                    <code
                      key={v}
                      className="mr-2"
                      style={{fontSize: '12px', whiteSpace: 'nowrap', lineHeight: '1.9'}}
                    >
                      {`{{ ${v} }}`}
                    </code>
                  ))}
                </div>
              </div>

              <Row>
                {/* Nome do Template */}
                <Col xs={12} sm={6} md={3} className="mb-3">
                  <Form.Group>
                    <Form.Label>Nome do Template</Form.Label>
                    <Form.Control
                      value={form.templateName || ''}
                      isInvalid={isInvalid('templateName')}
                      onChange={e => onChange('templateName', e.target.value)}
                    />
                    <Form.Control.Feedback type="invalid">Campo Obrigatório</Form.Control.Feedback>
                  </Form.Group>
                </Col>

                {/* Disparo */}
                <Col xs={12} sm={6} md={3} className="mb-3">
                  <Form.Group>
                    <Form.Label>Disparo</Form.Label>
                    <Form.Control
                      as="select"
                      value={form.shooting || ''}
                      isInvalid={isInvalid('shooting')}
                      onChange={e => onChange('shooting', e.target.value || null)}
                    >
                      <option value="">Selecione</option>
                      {shootingList.map(el => (
                        <option key={el.value} value={el.value}>{el.label}</option>
                      ))}
                    </Form.Control>
                    <Form.Control.Feedback type="invalid">Campo Obrigatório</Form.Control.Feedback>
                  </Form.Group>
                </Col>

                {/* Situação - Radio */}
                <Col xs={12} sm={6} md={3} className="mb-3">
                  <Form.Group>
                    <Form.Label>Situação</Form.Label>
                    <div>
                      <Form.Check
                        inline
                        type="radio"
                        label="Ativo"
                        id="status-active"
                        value="ACTIVE"
                        checked={form.status === 'ACTIVE'}
                        onChange={e => onChange('status', e.target.value)}
                      />
                      <Form.Check
                        inline
                        type="radio"
                        label="Inativo"
                        id="status-inactive"
                        value="INACTIVE"
                        checked={form.status === 'INACTIVE'}
                        onChange={e => onChange('status', e.target.value)}
                      />
                    </div>
                    {isInvalid('status') && (
                      <div className="text-danger" style={{fontSize: '0.875em'}}>Campo Obrigatório</div>
                    )}
                  </Form.Group>
                </Col>
              </Row>

              {/* Seção IntraFlow Whatsapp */}
              <Row>
                <Col xs={12}>
                  <small><b>IntraFlow Whatsapp</b></small>
                  <hr style={{marginTop: '1px'}}/>
                </Col>

                <Col xs={12} sm={6} md={3} className="mb-3">
                  <Form.Group>
                    <Form.Label>ID Canal</Form.Label>
                    <Form.Control
                      as="select"
                      value={form.channelId || ''}
                      isInvalid={isInvalid('channelId')}
                      onChange={e => onChange('channelId', e.target.value || null)}
                    >
                      <option value="">Filtrar</option>
                      {channels.map(el => (
                        <option key={el.value} value={el.value}>{el.label}</option>
                      ))}
                    </Form.Control>
                    <Form.Control.Feedback type="invalid">Campo Obrigatório</Form.Control.Feedback>
                  </Form.Group>
                </Col>

                <Col xs={12} sm={6} md={3} className="mb-3">
                  <Form.Group>
                    <Form.Label>Modelo Aprovado na Meta</Form.Label>
                    {metaTemplates.length > 0 ? (
                      <Form.Control
                        as="select"
                        value={form.whatsappTemplate || ''}
                        isInvalid={isInvalid('whatsappTemplate')}
                        onChange={e => onSelectTemplate(e.target.value)}
                      >
                        <option value="">Selecione</option>
                        {metaTemplates.map(el => (
                          <option key={el.value} value={el.value}>{el.label}</option>
                        ))}
                      </Form.Control>
                    ) : (
                      /* Sem lista da API o select ficaria vazio e travaria o cadastro,
                         então cai para entrada manual enquanto a IntraFlow não responde. */
                      <Form.Control
                        placeholder={form.channelId ? 'Digite o nome do modelo' : 'Escolha o canal primeiro'}
                        value={form.whatsappTemplate || ''}
                        isInvalid={isInvalid('whatsappTemplate')}
                        onChange={e => onChange('whatsappTemplate', e.target.value)}
                      />
                    )}
                    <Form.Control.Feedback type="invalid">Campo Obrigatório</Form.Control.Feedback>
                    {form.channelId && metaTemplates.length === 0 && (
                      <Form.Text className="text-muted">
                        Modelos deste canal indisponíveis — informe o nome manualmente.
                      </Form.Text>
                    )}
                  </Form.Group>
                </Col>

                <Col xs={12} md={6} className="mb-3">
                  <Form.Group>
                    <Form.Label>Variáveis do Modelo</Form.Label>
                    <div className="border rounded p-2">
                      {selectedTemplate && (
                        <div className="text-muted mb-2" style={{fontSize: '0.875em'}}>
                          {selectedTemplate.variableCount > 0
                            ? `Modelo com ${selectedTemplate.variableCount} variável(is) detectada(s) — mapeie cada {{n}} para uma variável do sistema.`
                            : 'Este modelo não possui variáveis.'}
                        </div>
                      )}
                      {paramRows.length === 0 ? (
                        !selectedTemplate && (
                          <div className="text-muted mb-2" style={{fontSize: '0.875em'}}>
                            Nenhuma variável configurada. Adicione uma linha para cada{' '}
                            <code>{'{{1}}'}</code>, <code>{'{{2}}'}</code>… do modelo aprovado.
                          </div>
                        )
                      ) : (
                        paramRows.map((row, i) => (
                          <Row key={i} className="align-items-center mb-2 mx-0">
                            <Col xs="auto" className="pl-0 pr-2">
                              <code style={{fontSize: '13px'}}>{`{{${i + 1}}}`}</code>
                            </Col>
                            <Col className="px-0">
                              <Form.Control
                                as="select"
                                size="sm"
                                value={row}
                                isInvalid={validated && !row.trim()}
                                onChange={e => setParamRows(
                                  paramRows.map((r, idx) => idx === i ? e.target.value : r)
                                )}
                              >
                                <option value="">Selecione a variável do sistema</option>
                                {layoutVars.map(v => (
                                  <option key={v} value={`{{ ${v} }}`}>{`{{ ${v} }}`}</option>
                                ))}
                              </Form.Control>
                            </Col>
                            {!selectedTemplate && (
                              <Col xs="auto" className="pl-2 pr-0">
                                <Button
                                  type="button"
                                  variant="outline-danger"
                                  size="sm"
                                  title="Remover"
                                  onClick={() => setParamRows(paramRows.filter((_, idx) => idx !== i))}
                                >
                                  &times;
                                </Button>
                              </Col>
                            )}
                          </Row>
                        ))
                      )}
                      {/* Com um modelo da API a quantidade de variáveis é fixa (vem do variableCount);
                          só na entrada manual do nome o admin controla as linhas na mão. */}
                      {!selectedTemplate && (
                        <Button
                          type="button"
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => setParamRows([...paramRows, ''])}
                        >
                          + Adicionar variável
                        </Button>
                      )}
                    </div>
                    <Form.Text className="text-muted">
                      A ordem importa: a 1ª linha preenche o <code>{'{{1}}'}</code> do modelo, a 2ª o{' '}
                      <code>{'{{2}}'}</code>, e assim por diante.
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>

              {/* Seção E-mail */}
              <Row>
                <Col xs={12}>
                  <small><b>E-mail</b></small>
                  <hr style={{marginTop: '1px'}}/>
                </Col>

                <Col xs={12} sm={6} md={3} className="mb-3">
                  <Form.Group>
                    <Form.Label>Enviar E-mail?</Form.Label>
                    <Form.Control
                      as="select"
                      value={form.sendEmail === false ? 'false' : 'true'}
                      onChange={e => onChange('sendEmail', e.target.value === 'true')}
                    >
                      <option value="true">Sim</option>
                      <option value="false">Não</option>
                    </Form.Control>
                  </Form.Group>
                </Col>

                {/* Com "Não" o e-mail é ignorado no disparo, então os campos ficam ocultos. */}
                {form.sendEmail !== false && (
                  <>
                    <Col xs={12} sm={6} md={3} className="mb-3">
                      <Form.Group>
                        <Form.Label>Assunto</Form.Label>
                        <Form.Control
                          value={form.emailSubject || ''}
                          isInvalid={isInvalid('emailSubject')}
                          onChange={e => onChange('emailSubject', e.target.value)}
                        />
                        <Form.Control.Feedback type="invalid">Campo Obrigatório</Form.Control.Feedback>
                      </Form.Group>
                    </Col>

                    <Col xs={12} className="mb-3">
                      <Form.Group>
                        <Form.Label>Corpo do E-mail</Form.Label>

                        <EmailEditor
                          ref={emailEditorRef}
                          onReady={onReadyEmailEditor}
                          minHeight="800px"
                          options={{locale: 'pt-BR'}}
                          style={{border: '1px solid #bfcbd9', borderRadius: '4px'}}
                        />
                      </Form.Group>
                    </Col>
                  </>
                )}
              </Row>

              <div>
                <Button variant="secondary" onClick={clearForm} className="mr-2">Limpar</Button>
                <Button variant="primary" type="submit">Gravar</Button>
              </div>
            </Card.Body>
          </Card>
        </Form>
      </div>
    </div>
  );
}
